import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import { useToast } from "@/contexts/ToastContext";
import { useAccessControl } from "@/hooks/useAccessControl";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Plus, Edit2, Trash2, FolderOpen, Eye, EyeOff, ShieldOff, Library } from "lucide-react";
import { collectionsApi, type Collection } from "@/lib/api/collections";
import { useCollections, COLLECTIONS_QUERY_KEY } from "@/hooks/useCollections";
import { cn } from "@/lib/utils";

const CollectionsSettingsPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isTenantAdmin } = useAccessControl();
  const [includeInactive, setIncludeInactive] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const { data: allCollections = [], isLoading: loading } = useCollections(true, {
    enabled: isTenantAdmin,
  });
  const collections = includeInactive ? allCollections : allCollections.filter((c) => c.isActive);
  const stats = {
    active: allCollections.filter((c) => c.isActive).length,
    inactive: allCollections.filter((c) => !c.isActive).length,
    total: allCollections.length,
  };

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [collectionToDelete, setCollectionToDelete] = useState<Collection | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleEdit = (collection: Collection) => {
    navigate(`/setup/collections/${collection.id}/edit`);
  };

  const openDeleteDialog = (collection: Collection) => {
    setCollectionToDelete(collection);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!collectionToDelete) return;

    try {
      setDeleting(true);
      const hardDelete = includeInactive || !collectionToDelete.isActive;
      await collectionsApi.delete(collectionToDelete.id, hardDelete);

      const actionWord = hardDelete ? "permanently deleted" : "deactivated";
      toast.success(`"${collectionToDelete.name}" has been ${actionWord}.`);
      queryClient.invalidateQueries(COLLECTIONS_QUERY_KEY);
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || "Unable to delete collection. Please try again."
      );
    } finally {
      setDeleting(false);
      setCollectionToDelete(null);
    }
  };

  const handleNewClick = () => {
    navigate("/setup/collections/new");
  };

  // Filter collections by search query
  const filteredCollections = collections.filter(
    (collection) =>
      collection.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      collection.code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      collection.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isTenantAdmin) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen bg-[#F8F9FA]">
          <Card className="max-w-md p-8 text-center">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
              <ShieldOff className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="mb-2 text-xl font-bold text-gray-900">Access Denied</h2>
            <p className="text-gray-600">
              You need administrator permissions to manage collections.
            </p>
          </Card>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageContainer width="wide">
        <PageHeader
          title="Collections"
          description="Group library materials into special collections for easier organization"
          breadcrumbItems={[{ label: "Setup", href: "/setup" }, { label: "Collections" }]}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search by name, code, or description..."
        />

        {/* Quick Info Cards */}
        <div className="grid grid-cols-1 gap-4 mb-6 md:grid-cols-3">
          <Card className="p-4 bg-white border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <FolderOpen className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
                <p className="text-sm text-gray-500">Active Collections</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-50">
                <EyeOff className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.inactive}</p>
                <p className="text-sm text-gray-500">Inactive Collections</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-white border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-teal-50">
                <Library className="w-5 h-5 text-teal-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                <p className="text-sm text-gray-500">Total Collections</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Actions Bar */}
        <div className="flex flex-col justify-between gap-4 mb-6 sm:flex-row sm:items-center">
          <div className="flex items-center gap-3">
            <Checkbox
              id="includeInactive"
              checked={includeInactive}
              onCheckedChange={(checked) => setIncludeInactive(checked === true)}
            />
            <Label
              htmlFor="includeInactive"
              className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer"
            >
              {includeInactive ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              Show inactive collections
            </Label>
          </div>
          <Button onClick={handleNewClick} className="shadow-sm">
            <Plus className="w-4 h-4 mr-2" />
            Add Collection
          </Button>
        </div>

        {/* Collections List */}
        <Card className="overflow-hidden bg-white shadow-sm">
          {loading ? (
            <div className="py-16">
              <LoadingSpinner size="lg" label="Loading collections..." />
            </div>
          ) : filteredCollections.length === 0 ? (
            <div className="py-16 text-center">
              <div className="flex items-center justify-center w-16 h-16 p-4 mx-auto mb-4 bg-gray-100 rounded-full">
                <FolderOpen className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-gray-900">
                {searchQuery ? "No matching collections" : "No collections yet"}
              </h3>
              <p className="max-w-sm mx-auto mb-6 text-sm text-gray-500">
                {searchQuery
                  ? "Try a different search term or check for typos."
                  : "Create your first collection to group related materials together."}
              </p>
              {!searchQuery && (
                <Button onClick={handleNewClick} variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Collection
                </Button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredCollections.map((collection) => (
                <div
                  key={collection.id}
                  className={cn(
                    "p-5 hover:bg-gray-50 transition-colors",
                    !collection.isActive && "bg-gray-50/50"
                  )}
                >
                  <div className="flex items-start justify-between gap-4">
                    {/* Left: Main Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900 truncate">
                          {collection.name}
                        </h3>
                        {collection.code && (
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded">
                            {collection.code}
                          </span>
                        )}
                        <span
                          className={cn(
                            "px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap",
                            collection.isActive
                              ? "bg-green-100 text-green-700"
                              : "bg-gray-200 text-gray-600"
                          )}
                        >
                          {collection.isActive ? "Active" : "Inactive"}
                        </span>
                      </div>

                      {collection.description && (
                        <p className="text-sm text-gray-500 line-clamp-2">
                          {collection.description}
                        </p>
                      )}
                    </div>

                    {/* Right: Actions */}
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(collection)}
                        className="gap-1.5"
                      >
                        <Edit2 className="w-4 h-4" />
                        <span className="hidden sm:inline">Edit</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openDeleteDialog(collection)}
                        className="gap-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 hover:border-red-200"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span className="hidden sm:inline">
                          {collection.isActive ? "Deactivate" : "Delete"}
                        </span>
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Help Text */}
        <div className="p-4 mt-6 bg-white border border-gray-200 rounded-lg">
          <h4 className="mb-1 font-medium text-gray-900">What are collections?</h4>
          <p className="text-sm text-gray-600">
            Collections help you organize materials that share something in common, such as a "Local
            History Archive," "Rare Books," or "Children's Corner." Items can belong to a collection
            in addition to being in a physical location.
          </p>
        </div>
      </PageContainer>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={
          collectionToDelete?.isActive
            ? `Deactivate "${collectionToDelete?.name}"?`
            : `Delete "${collectionToDelete?.name}"?`
        }
        description={
          collectionToDelete?.isActive ? (
            <>
              <p className="mb-2">
                Deactivating this collection will hide it from selection lists. Items already in
                this collection will not be affected.
              </p>
              <p className="font-medium text-gray-600">You can reactivate it later if needed.</p>
            </>
          ) : (
            <>
              <p className="mb-2">
                This will <strong>permanently delete</strong> this collection. This action cannot be
                undone.
              </p>
              <p className="font-medium text-red-600">
                Make sure no items are currently assigned to this collection.
              </p>
            </>
          )
        }
        confirmLabel={collectionToDelete?.isActive ? "Deactivate" : "Delete Permanently"}
        cancelLabel="Cancel"
        onConfirm={handleDelete}
        variant="danger"
        loading={deleting}
      />
    </AppLayout>
  );
};

export default CollectionsSettingsPage;
